import React, { Component } from 'react';
import Switch from 'react-switch';
import { useParams, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

function withParams(Component) {
  return props => {
    const params = useParams();
    const navigate = useNavigate();
    return <Component {...props} params={params} navigate={navigate} />;
  };
}

class RuleEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      actions: [
        'Block Transaction',
        'Flag for Review',
        'Monitor',
        'Log Event',
        'Escalate Risk',
        'Request Additional Info',
        'Queue'
      ],
      selectedAction: 'Block Transaction',
      ruleComponents: [],
      logicalOperators: [],
      ruleEnabled: false,
      ruleName: '',
      description: '',
      savedRules: [],
      thresholdForAlerts: '',
      businessObjects: [],         // Dynamically loaded business objects
      attributes: {},              // Attributes grouped by business object
      operatorGroups: {},          // Operators grouped by operator group name
      selectedBusinessObject: '',  // Currently selected business object
      selectedOperatorGroup: '',   // Currently selected operator group
      loading: true,               // Overall loading state
      loadingBusinessObjects: true, // Loading state for business objects
      loadingAttributes: true,     // Loading state for attributes
      loadingOperatorGroups: true, // Loading state for operator groups
    };

    this.loadRuleForEditing = this.loadRuleForEditing.bind(this);
    this.calculateCompositeRiskScore = this.calculateCompositeRiskScore.bind(this);
  }

  componentDidMount() {
    Promise.all([
      this.loadBusinessObjects(),
      this.loadAttributes(),
      this.loadOperatorGroups(),
      this.loadRuleForEditing(),
    ])
      .then(() => this.setState({ loading: false }))
      .catch((error) => {
        console.error('Error during initialization:', error);
        this.setState({ loading: false });
      });
  }
  
generateRuleDataJSON = (conditions) => {
  console.log("Conditions received by generateRuleDataJSON:", conditions);

  return conditions.map((condition, index) => {
    console.log(`Processing condition ${index + 1}:`, condition);

    const {
      operatorGroup = "", // Extract operator group
      attributes = [],    // Extract attributes array
      operator = "",
      value = null,
    } = condition;

    // Map operatorGroup to group type
    const groupMappings = {
      math: "float",
      string: "string",
      date: "datetime",
      logical: "boolean",
      array: "array",
    };

    const group = groupMappings[operatorGroup] || ""; // Map operatorGroup to group

    // Construct the comparison object
    const comparison = {
      group, // Use mapped group
      object_1: attributes[0]
        ? {
            object: attributes[0].businessObject || "",
            attribute: attributes[0].attribute || "",
            label: attributes[0].label || "",
            allow_null: attributes[0].allowNull ?? false,
            source: attributes[0].source || "payload",
            attribute_type: attributes[0].attributeType || "",
          }
        : {}, // Default to an empty object if attributes[0] is missing
      operator: operator || "",
    };

    // Add secondary object if a second attribute is present
    if (attributes.length === 2) {
      comparison.object_2 = {
        object: attributes[1].businessObject || "",
        attribute: attributes[1].attribute || "",
        label: attributes[1].label || "",
        allow_null: attributes[1].allowNull ?? false,
        source: attributes[1].source || "payload",
        attribute_type: attributes[1].attributeType || "",
      };
    } else if (value !== null && attributes.length === 1) {
      // Add value only when it's a single-attribute condition
      comparison.value = value;
    }

    console.log(`Generated comparison for condition ${index + 1}:`, comparison);

    return {
      type: "condition",
      comparison,
    };
  });
};





  loadBusinessObjects() {
    return fetch('/composite-backend/internal/ui/rules/get-all-business-objects')
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          toast.error('Failed to load business objects.');
        } else {
          const businessObjects = data.map((obj) => obj.objectType);
          this.setState({ businessObjects });
          console.log('Loaded Business Objects:', this.state.businessObjects);
        }
      })
      .catch((error) => {
        console.error('Error fetching business objects:', error);
        toast.error('Error fetching business objects.');
      });
  }
  loadAttributes() {
    return fetch('/composite-backend/internal/ui/rules/get-all-business-attributes')
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          toast.error('Failed to load attributes.');
        } else {
          const attributes = data.reduce((acc, attr) => {
            acc[attr.businessObject] = acc[attr.businessObject] || [];
            acc[attr.businessObject].push(attr.attributeName);
            return acc;
          }, {});
          this.setState({ attributes });
          console.log('Loaded attributes:', this.state.attributes);
        }
      })
      .catch((error) => {
        console.error('Error fetching attributes:', error);
        toast.error('Error fetching attributes.');
      });
  }
  loadOperatorGroups() {
  return fetch('/composite-backend/internal/ui/rules/get-all-operator-groups')
    .then((response) => response.json())
    .then((data) => {
      const operatorGroups = data.reduce((acc, group) => {
        acc[group.operatorGroupName] = acc[group.operatorGroupName] || [];
        acc[group.operatorGroupName].push(group.operator);
        return acc;
      }, {});
      this.setState({ operatorGroups }, () => {
        console.log('Loaded operator groups:', this.state.operatorGroups);
      });
    })
    .catch((error) => {
      console.error('Error fetching operator groups:', error);
      toast.error('Failed to load operator groups.');
    });
}


loadRuleForEditing() {
  const { ruleId } = this.props.params;

  if (ruleId) {
    return fetch(`/composite-backend/internal/ui/rules/get-rule?ruleName=${ruleId}`)
      .then((response) => response.json())
      .then((data) => {
        console.log("Loaded Rule from Backend:", data);

        // Ensure components exist and are an array
        if (!Array.isArray(data.components)) {
          console.error("components is not an array.");
          return;
        }

        // Map components into ruleComponents
        const components = data.components.map((component) => {
          if (!component.comparison) {
            console.error("Missing comparison object in component:", component);
            return null;
          }

          const { group, object_1, operator, value, object_2 } = component.comparison;

          // Determine whether it's a single-attribute condition or a comparison
          const attributes = [];
          if (object_1) {
            attributes.push({
              businessObject: object_1.object || "",
              attribute: object_1.attribute || "",
              label: object_1.label || "",
              allowNull: object_1.allow_null ?? false,
              source: object_1.source || "payload",
              attributeType: object_1.attribute_type || "",
            });
          }

          if (object_2) {
            attributes.push({
              businessObject: object_2.object || "",
              attribute: object_2.attribute || "",
              label: object_2.label || "",
              allowNull: object_2.allow_null ?? false,
              source: object_2.source || "payload",
              attributeType: object_2.attribute_type || "",
            });
          }

          // Handle conditions with a value or attribute-to-attribute comparison
          return {
            type: component.type || "condition",
            attributes, // Handles both object_1 and object_2
            operatorGroup: this.mapGroupToOperatorGroup(group),
            operator: operator || "",
            value: value || "", // Include value for single-attribute conditions
          };
        }).filter(Boolean); // Remove null or invalid components

        console.log("Mapped Rule Components:", components);
        // Fix logical operators persistence issue
        let logicalOperators = data.logicalOperators;
        if (!Array.isArray(logicalOperators) || logicalOperators.length !== components.length - 1) {
          logicalOperators = new Array(components.length - 1).fill("AND"); // Ensure the correct number of logical operators
        }

        // Update the state
        this.setState({
          ruleName: data.name || "",
          description: data.description || "",
          ruleComponents: components,
          selectedAction: data.action || "",
          ruleEnabled: data.enabled || false,
          logicalOperators: logicalOperators, // Persist logical operators correctly,
        });
      })
      .catch((error) => {
        console.error("Error loading the rule:", error);
        toast.error("Failed to load the rule.");
      });
  }
}


mapGroupToOperatorGroup = (group) => {
  const groupMappings = {
    float: "math",
    integer: "math",
    string: "string",
    datetime: "date",
    boolean: "logical",
    array: "array",
  };
  return groupMappings[group] || "";
};


  getBusinessObjectAttributes(businessObject) {
  return this.state.attributes[businessObject] || [];
}

  getAllBusinessObjects = () => {
  return this.state.businessObjects || []; // Return the array or fallback to an empty array
};


  getOperatorGroups = () => {
  return this.state.operatorGroups || []; // Return operator group as an array
};

  addAttributeToCondition = (index) => {
    const updatedComponents = this.state.ruleComponents.map((component, i) => {
      if (i === index && component.attributes.length < 2) {
        return {
          ...component,
          attributes: [
            ...component.attributes,
            { businessObject: '', attribute: '' },
          ],
        };
      }
      return component;
    });
    this.setState({ ruleComponents: updatedComponents });
  };

   handleAttributeChange = (componentIndex, attributeIndex, type, value) => {
    const updatedComponents = this.state.ruleComponents.map((component, i) => {
      if (i === componentIndex) {
        const updatedAttributes = component.attributes.map((attribute, j) => {
          if (j === attributeIndex) {
            return { ...attribute, [type]: value };
          }
          return attribute;
        });
        return { ...component, attributes: updatedAttributes };
      }
      return component;
    });
    this.setState({ ruleComponents: updatedComponents });
  };

  handleOperatorGroupChange = (index, value) => {
    const updatedComponents = this.state.ruleComponents.map((component, i) =>
      i === index ? { ...component, operatorGroup: value, operator: '' } : component
    );
    this.setState({ ruleComponents: updatedComponents });
  };

 addComponentToRule = () => {
  const newComponent = {
    attributes: [{ businessObject: '', attribute: '' }], // Default attribute structure
    operatorGroup: '', // Group like 'math', 'string', etc.
    operator: '', // Operator like 'Equal To', 'Contains', etc.
    value: '', // Condition value
    riskScoreWeight: 0, // Default weight
  };
  this.setState((prevState) => ({
    ruleComponents: [...prevState.ruleComponents, newComponent],
  }));
};

updateComponentValue = (index, field, value) => {
  const updatedComponents = this.state.ruleComponents.map((component, i) =>
    i === index
      ? {
          ...component,
          [field]: value,
        }
      : component
  );
  this.setState({ ruleComponents: updatedComponents });
};

removeComponent = (index) => {
  const updatedComponents = this.state.ruleComponents.filter((_, i) => i !== index);
  const updatedLogicalOperators = this.state.logicalOperators.filter((_, i) => i !== index);
  this.setState({
    ruleComponents: updatedComponents,
    logicalOperators: updatedLogicalOperators,
  });
};

validateComponents = () => {
  const { ruleComponents } = this.state;

  for (let i = 0; i < ruleComponents.length; i++) {
    const { attributes, operatorGroup, operator, value } = ruleComponents[i];

    if (!attributes || attributes.length === 0 || !attributes[0].attribute || !attributes[0].businessObject) {
      alert(`Component ${i + 1} is missing attributes.`);
      return false;
    }

    if (!operatorGroup) {
      alert(`Component ${i + 1} is missing an operator group.`);
      return false;
    }

    if (!operator) {
      alert(`Component ${i + 1} is missing an operator.`);
      return false;
    }

    // If only one attribute is present, value is required (unless it's "Is Empty" or "Is Not Empty")
    if (attributes.length === 1 && !value && operator !== 'Is Empty' && operator !== 'Is Not Empty') {
      alert(`Component ${i + 1} is missing a value.`);
      return false;
    }

    // If two attributes are present, no value is needed
    if (attributes.length === 2 && (!attributes[1].attribute || !attributes[1].businessObject)) {
      alert(`Component ${i + 1} has an incomplete second attribute.`);
      return false;
    }
  }

  return true;
};

validateLogicalOperators = () => {
  const { logicalOperators, ruleComponents } = this.state;

  if (ruleComponents.length > 1 && logicalOperators.length !== ruleComponents.length - 1) {
    alert('Please specify logical operators between all conditions.');
    return false;
  }

  return true;
};

saveRule = () => {
  const {
    selectedAction,
    ruleName,
    description,
    ruleComponents,
    logicalOperators,
    ruleEnabled,
    thresholdForAlerts,
  } = this.state;

  console.log("Starting saveRule..."); // Initial log for function entry

  // Validate rule name and description
  if (!ruleName) {
    console.error("Validation Failed: Rule name is required.");
    console.log("Rule Name:", ruleName);
    toast.error('Please enter a rule name.');
    return;
  }

  if (!description) {
    console.error("Validation Failed: Description is required.");
    console.log("Description:", description);
    toast.error('Please enter a description.');
    return;
  }

  // Validate components
  if (!this.validateComponents()) {
    console.error("Validation Failed: Invalid components.");
    console.log("Current Rule Components:", ruleComponents);
    return;
  }

  // Validate logical operators
  if (!this.validateLogicalOperators()) {
    console.error("Validation Failed: Invalid logical operators.");
    console.log("Logical Operators:", logicalOperators);
    return;
  }

  // Validate composite risk score
  const compositeRiskScore = this.calculateCompositeRiskScore();
  if (thresholdForAlerts && compositeRiskScore > parseFloat(thresholdForAlerts)) {
    console.error(
      `Validation Failed: Composite risk score (${compositeRiskScore}) exceeds threshold (${thresholdForAlerts}).`
    );
    toast.error('Composite risk score exceeds the alert threshold.');
    return;
  }

  // Generate rule data JSON
  console.log("Generating ruleData JSON...");
  const ruleData = this.generateRuleDataJSON(ruleComponents);
  console.log("Generated ruleData JSON:", ruleData);

  // Construct the payload
  const newRule = {
    name: ruleName,
    description,
    action: selectedAction,
    enabled: ruleEnabled,
    logicalOperators,
    rule_data: ruleData,
  };

  console.log("Payload for saveRule:", newRule);
  const token = localStorage.getItem("access_token"); // Get token from storage
  // Set loading state
  this.setState({ isLoading: true }, () => {
    console.log("Loading state set to:", this.state.isLoading);

    // Save the rule to the server
    console.log("Making fetch call with payload...");
    fetch('/composite-backend/internal/ui/rules/save-rule', {
      method: 'POST',
      credentials: "include",  // ✅ Ensures cookies are sent
      headers: {
        'Content-Type': 'application/json',
        "Authorization": `Bearer ${token}`,  // ✅ Also send as backup
      },
      body: JSON.stringify(newRule),
    })
      .then((response) => {
        console.log("Response received:", response);
        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log("Response data:", data);
        if (data.error) {
          console.error("API Error:", data.error);
          toast.error(`Error saving rule: ${data.error}`);
        } else {
          console.log("Rule saved successfully!");
          toast.success('Rule saved successfully!');
          this.props.navigate('/');
        }
      })
      .catch((error) => {
        console.error("Error during fetch:", error);
        toast.error('Failed to save the rule. Check console for details.');
      })
      .finally(() => {
        console.log("Resetting loading state.");
        this.setState({ isLoading: false });
      });
  });
};




  calculateCompositeRiskScore() {
    return this.state.ruleComponents.reduce((total, component) => total + (parseFloat(component.riskScoreWeight) || 0), 0);
  }

  render() {
    const { selectedAction, ruleComponents, logicalOperators, ruleEnabled, ruleName, description, thresholdForAlerts } = this.state;
    const operatorGroups = this.getOperatorGroups();
    const businessObjects = this.getAllBusinessObjects();

    return (
      <div>
        {/* Header Section */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#007BFF', padding: '16px', color: '#FFF' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img src="/assets/b4d48a3e-5b5a-4392-ab30-df41c0a4471a.jpg" alt="Idenza Logo" style={{ width: '50px', marginRight: '10px' }} />
                    <h1 style={{ margin: '0' }}>Idenza</h1>
                </div>
                <h2 style={{ textAlign: 'center', flexGrow: 1 }}>Rules Management</h2>
            </div>
            
            {/* Back to Rules Listing Button */}
  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
  <button
    onClick={() => this.props.navigate('/')}
    style={{
      backgroundColor: '#007BFF',
      color: '#FFFFFF',
      padding: '10px 20px',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 'bold',
    }}
  >
    Back to Rules Listing
  </button>
</div>


            {/* Main Content */}
            <div style={{ padding: '24px' }}>
                <h2></h2>


          <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <h3>Enter Rule Name</h3>
              <input
                type="text"
                value={ruleName}
                onChange={(e) => this.setState({ ruleName: e.target.value })}
                placeholder="Rule Name"
                maxLength={100}
                style={{ padding: '8px', width: '100%', borderRadius: '4px', border: '1px solid #D1D5DB' }}
              />
            </div>
            <div style={{ flex: 1 }}>
                  <h3>Enter Description</h3>
                  <textarea
                    rows={3}
                    value={description}
                    onChange={(e) => this.setState({ description: e.target.value })}
                    placeholder="Describe the rule's logic, thresholds, and business intent"
                    maxLength={300}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                  <div style={{ textAlign: 'right', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {description.length} / 300
                  </div>
                </div>

          </div>

          <div>
            <h3>Rule Builder</h3>
            {ruleComponents.length > 0 ? (
              ruleComponents.map((component, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  {/* Render the First Attribute */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <select
                      value={component.attributes[0]?.businessObject || ''}
                      onChange={(e) => this.handleAttributeChange(index, 0, 'businessObject', e.target.value)}
                      style={{ padding: '4px', width: '150px', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                    >
                      <option value="">Select Business Object</option>
                      {businessObjects.map((businessObject, i) => (
                        <option key={i} value={businessObject}>{businessObject}</option>
                      ))}
                    </select>

                    <select
                      value={component.attributes[0]?.attribute || ''}
                      onChange={(e) => this.handleAttributeChange(index, 0, 'attribute', e.target.value)}
                      style={{ padding: '4px', width: '200px', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                    >
                      <option value="">Select Attribute</option>
                      {this.getBusinessObjectAttributes(component.attributes[0]?.businessObject || '').map((attribute, i) => (
                        <option key={i} value={attribute}>{attribute}</option>
                      ))}
                    </select>
                  </div>

                  {/* Render Operator Group and Operator */}
                  {component.attributes.length >= 1 && (
                    <>
                      <select
                        value={component.operatorGroup || ''}
                        onChange={(e) => this.handleOperatorGroupChange(index, e.target.value)}
                        style={{ marginLeft: '8px', padding: '4px', width: '120px', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                      >
                        <option value="">Select Operator Group</option>
                        <option value="math">Math</option>
                        <option value="string">String</option>
                        <option value="date">Date</option>
                        <option value="logical">Logical</option>
                        <option value="array">Array</option>
                      </select>

                      <select
                        value={component.operator || ''}
                        onChange={(e) => this.updateComponentValue(index, 'operator', e.target.value)}
                        style={{ marginLeft: '8px', padding: '4px', width: '120px', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                        disabled={!component.operatorGroup}
                      >
                        <option value="">Select Operator</option>
                        {(operatorGroups[component.operatorGroup] || []).map((op, i) => (
                          <option key={i} value={op}>{op}</option>
                        ))}
                      </select>

                      {/* Render the value field only if there's a single attribute */}
                      {component.attributes.length === 1 && (
                        <input
                          type="text"
                          value={component.value || ''}
                          onChange={(e) => this.updateComponentValue(index, 'value', e.target.value)}
                          placeholder="Value"
                          style={{ marginLeft: '8px', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      )}
                    </>
                  )}

                  {/* Render the Second Attribute */}
                  {component.attributes.length === 2 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                      <select
                        value={component.attributes[1]?.businessObject || ''}
                        onChange={(e) => this.handleAttributeChange(index, 1, 'businessObject', e.target.value)}
                        style={{ padding: '4px', width: '150px', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                      >
                        <option value="">Select Business Object</option>
                        {businessObjects.map((businessObject, i) => (
                          <option key={i} value={businessObject}>{businessObject}</option>
                        ))}
                      </select>

                      <select
                        value={component.attributes[1]?.attribute || ''}
                        onChange={(e) => this.handleAttributeChange(index, 1, 'attribute', e.target.value)}
                        style={{ padding: '4px', width: '200px', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                      >
                        <option value="">Select Attribute</option>
                        {this.getBusinessObjectAttributes(component.attributes[1]?.businessObject || '').map((attribute, i) => (
                          <option key={i} value={attribute}>{attribute}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Add Attribute Button */}
                  <button
                    onClick={() => this.addAttributeToCondition(index)}
                    style={{ marginLeft: '8px', backgroundColor: '#10B981', color: '#FFF', padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    disabled={component.attributes.length >= 2} // Disable after adding the second attribute
                  >
                    + Add Attribute
                  </button>

                  {/* Remove Component Button */}
                  <button
                    onClick={() => this.removeComponent(index)}
                    style={{ marginLeft: '8px', backgroundColor: 'transparent', border: 'none', color: '#FF5A5F', fontWeight: 'bold', fontSize: '1.2rem', cursor: 'pointer' }}
                  >
                    ×
                  </button>

                  {/* Logical Operators between Conditions */}
                  {index < ruleComponents.length - 1 && (
                    <select
                      value={logicalOperators[index] || ''}
                      onChange={(e) => this.setState((state) => {
                        const logicalOperators = [...state.logicalOperators];
                        logicalOperators[index] = e.target.value;
                        return { logicalOperators };
                      })}
                      style={{ marginLeft: '8px', padding: '2px 4px', fontSize: '0.8rem', width: '80px', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                    >
                      <option value="">Select</option>
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                      <option value="NOT">NOT</option>
                    </select>
                  )}
                </div>
              ))
            ) : (
              <p>No components added yet. Click below to add a new condition.</p>
            )}

            {/* Add New Condition Button */}
            <button
                  onClick={this.addComponentToRule}
                  style={{
                    marginTop: '16px',
                    backgroundColor: '#007BFF',
                    color: '#FFF',
                    padding: '10px 20px',
                    borderRadius: '999px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease-in-out'
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#0056b3')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#007BFF')}
                >
                  ➕ Add Condition
                </button>

          </div>

          {/* Rule Review and Save Section */}
          <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#F3F4F6' }}>
                      <h3 style={{ marginBottom: '4px' }}>🧠 Rule Logic Preview</h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
              Based on selected conditions and logical operators
            </p>

            <ul>
              {ruleComponents.map((component, index) => (
               <li key={index} style={{ marginBottom: '8px', lineHeight: '1.4' }}>
               <strong>{component.attributes.map(attr => `${attr.businessObject}.${attr.attribute}`).join(' AND ')}</strong>
               {' '}
               <span style={{ fontStyle: 'italic' }}>
                 {component.operatorGroup} {component.operator}
               </span>
               {' '}
               {component.value !== null && component.value !== '' ? `"${component.value}"` : ''}
               {logicalOperators[index] && (
                 <span style={{ marginLeft: '8px', color: '#007BFF' }}>
                   {logicalOperators[index]}
                 </span>
               )}
             </li>             
              ))}
            </ul>

            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '16px' }}>
              <div>
                <label>Composite Risk Score:</label>
                <input
                  type="number"
                  value={this.calculateCompositeRiskScore()}
                  readOnly
                  style={{ padding: '8px', width: '100%', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                />
              </div>
              <div>
                <label>Threshold for Alerts:</label>
                <input
                  type="number"
                  value={thresholdForAlerts}
                  onChange={(e) => this.setState({ thresholdForAlerts: e.target.value })}
                  placeholder="Enter threshold for alerts"
                  style={{ padding: '8px', width: '100%', borderRadius: '4px', border: '1px solid #D1D5DB' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <label>Action:</label>
              <select
                value={selectedAction}
                onChange={(e) => this.setState({ selectedAction: e.target.value })}
                style={{ padding: '8px', width: '100%', borderRadius: '4px', border: '1px solid #D1D5DB', marginTop: '8px' }}
              >
                {this.state.actions.map((action, i) => (
                  <option key={i} value={action}>{action}</option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center' }}>
              <Switch
                onChange={(checked) => this.setState({ ruleEnabled: checked })}
                checked={ruleEnabled}
                className="react-switch"
                onColor="#4CAF50"
              />
              <span style={{ marginLeft: '8px' }}>Enable Rule</span>
            </div>

            <button onClick={this.saveRule} style={{ marginTop: '24px', backgroundColor: '#10B981', color: '#FFFFFF', padding: '12px', borderRadius: '8px', width: '100%', fontWeight: 'bold', cursor: 'pointer', border: 'none' }}>
              Save Rule
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default withParams(RuleEditor);

